import React, { FC, useEffect, useState } from "react";
import Input from "shared/Input/Input";
import Select from "shared/Select/Select";
import CommonLayout from "./CommonLayout";
import FormItem from "./FormItem";
import { PlaceAutocomplete } from "components/GoogleMaps/PlaceAutocomplete";
import type { PlaceResult } from "components/GoogleMaps/PlaceAutocomplete";
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentProperty, setProperty } from "application/features/property/propertySlice";
import { useLazyGetBorgoQuery } from "application/features/borghi/borghiApiSlice";
import AddressInputMiniMap from "components/GoogleMaps/AddressInputMiniMap";

export interface PageAddListing2Props { }

const PageAddListing2: FC<PageAddListing2Props> = () => {

  const [next, setNext] = useState("/host/new-property/add-listing-3");
  const [getBorgo, { data, isLoading }] = useLazyGetBorgoQuery({});

  const property = useSelector(selectCurrentProperty);

  const dispatch = useDispatch();

  const handleUpdateProperty = (field: string, value: any) => {
    try {
      dispatch(setProperty({ ...property, [field]: value }))
    } catch (e) {
      console.log(e)
    }
  };

  const handleGetBorgo = async (city: string) => {
    try {
      const data = await getBorgo(city.toLowerCase()).unwrap();
      if (!data?.borgo?.name) {
        setNext("/host/new-property/add-listing-borgo-intro")
      } else {
        setNext("/host/new-property/add-listing-3")
      }
    } catch (e) {
      console.log(e)
    }
  };

  useEffect(() => {
    if (property.city) {
      handleGetBorgo(property.city)
    }
  }, [property.city])

  useEffect(() => {
    handleUpdateProperty("state", "italy");
  }, []);

  const [selectedPlace, setSelectedPlace] =
    useState<PlaceResult | null>({
      latitude: property?.location?.latitude || 41.9027,
      longitude: property?.location?.longitude || 12.4963,
  });

  const buildingInputRef = React.useRef<HTMLInputElement>(null);
  const cityInputRef = React.useRef<HTMLInputElement>(null);
  const countryInputRef = React.useRef<HTMLInputElement>(null);
  const postalCodeInputRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => {

    if (!selectedPlace) {
      return;
    }

    const updatedProperty = {
      ...property,
      location: {
        latitude: selectedPlace.latitude,
        longitude: selectedPlace.longitude,
      },
      address: selectedPlace.formatted_address,
      city: selectedPlace.city,
      // Backend requires `state` = Italian region (administrative_area_level_1),
      // and `country` = the country name. Previously these were swapped.
      state: selectedPlace.region,
      postal_code: selectedPlace.postal_code,
      country: selectedPlace.country,
      region: selectedPlace.region,
      province: selectedPlace.province,
      place_id: selectedPlace.place_id,
    };

    // Automatically update the form fields based on the selected place
    if (buildingInputRef.current) {
      buildingInputRef.current.value = selectedPlace.street_number || "";
    }
    if (cityInputRef.current) {
      cityInputRef.current.value = selectedPlace.city || "";
    }
    if (countryInputRef.current) {
      countryInputRef.current.value = selectedPlace.country || "";
    }
    if (postalCodeInputRef.current) {
      postalCodeInputRef.current.value = selectedPlace.postal_code || "";
    }

    dispatch(setProperty(updatedProperty));

  }, [selectedPlace]);

  return (
    <CommonLayout
      index="02"
      nextHref={next}
      backtHref="/host/new-property/add-listing-1"
      canGoNext={(selectedPlace.country !== undefined) && (selectedPlace.city !== undefined) && (selectedPlace.postal_code !== undefined)}
    >
      <>
        <h2 className="text-2xl font-semibold">Dove si trova il tuo alloggio?</h2>
        <div className="w-14 border-b border-neutral-200 dark:border-neutral-700"></div>
        {/* FORM */}
        <div className="space-y-8">
          {/* ITEM */}
          <FormItem label="Nazione">
            <Select
              onChange={(e) => handleUpdateProperty("country", e.target.value)}
            >
              <option value="italy">Italia</option>
              <option value="spain">Spagna</option>
              <option value="france">Francia</option>
              <option value="portugal">Portogallo</option>
              <option value="germany">Germania</option>
            </Select>
          </FormItem>
          <FormItem label="Indirizzo">
            <PlaceAutocomplete
              onPlaceSelect={setSelectedPlace}
              placeholder="Cerca un indirizzo"
            />
          </FormItem>
          <FormItem label="Piano, Interno, Palazzina (facoltativo)">
            <Input
              onChange={(e) => handleUpdateProperty("building", e.target.value)}
              value={property?.building || ""}
              ref={buildingInputRef}
            />
          </FormItem>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-5">
            <FormItem label="Città">
              <Input
                onChange={(e) => handleUpdateProperty("city", e.target.value)}
                value={property?.city || ""}
                ref={cityInputRef}
              />
            </FormItem>
            <FormItem label="Provincia">
              <Input
                onChange={(e) => handleUpdateProperty("country", e.target.value)}
                value={property?.country || ""}
                ref={countryInputRef}
              />
            </FormItem>
            <FormItem label="CAP">
              <Input
                onChange={(e) => handleUpdateProperty("postal_code", e.target.value)}
                value={property?.postal_code || ""}
                ref={postalCodeInputRef}
              />
            </FormItem>
          </div>
          <div>
            <div className="my-4">
              <div className="aspect-w-5 aspect-h-5 sm:aspect-h-3">
                <AddressInputMiniMap
                  className=""
                  defaultCenter={{ lat: selectedPlace.latitude, lng: selectedPlace.longitude }}
                  place={selectedPlace}
                />
              </div>
            </div>
          </div>
        </div>
      </>
    </CommonLayout>
  );
};

export default PageAddListing2;
